import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";
import { Sparkles, X, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * RestockRevealWatcher — global watcher that celebrates when a member's
 * wishlisted drop restocks while they're browsing.
 *
 * Strategy (100% client-side, no backend endpoint needed):
 *   1. Poll /drops/me/favorites every 60s while user is signed-in member.
 *   2. Compare each favorite's current availability against a localStorage
 *      snapshot ("was sold-out last visit, is available now" → restock!).
 *   3. Fire canvas-confetti + show a top-right toast card linking to the drop.
 *   4. Persist the new snapshot so we don't re-fire.
 *
 * The waitlist SMS/email ping already went out server-side via
 * `_dispatch_waitlist_restock`. This is the in-app celebratory reveal.
 */

const STORAGE_KEY = "ff2d_restock_snapshot_v1";
const POLL_MS = 60_000;

function loadSnapshot() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function saveSnapshot(snap) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snap));
  } catch { /* noop */ }
}

function isAvailable(drop) {
  const remaining = (drop.total_inventory || 0) - (drop.sold || 0);
  const status = drop.status || "";
  // Available if there's stock AND status is not sold_out / ended.
  return remaining > 0 && status !== "sold_out" && status !== "ended";
}

function fireConfetti() {
  // Two bursts from left+right for a satisfying celebratory feel
  const end = Date.now() + 900;
  const colors = ["#1B3B2B", "#D4B895", "#8B2626", "#F9F6F0"];
  (function frame() {
    confetti({
      particleCount: 3,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.7 },
      colors,
    });
    confetti({
      particleCount: 3,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.7 },
      colors,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

export default function RestockRevealWatcher() {
  const { user } = useAuth();
  const [toast, setToast] = useState(null); // {slug, name, image_url}
  const timerRef = useRef(null);
  const dismissRef = useRef(null);

  const check = useCallback(async () => {
    if (!user?.is_member) return;
    let data;
    try {
      const r = await api.get("/drops/me/favorites");
      data = r.data;
    } catch {
      return;
    }
    const drops = data?.drops || [];
    const snap = loadSnapshot();
    const nextSnap = {};
    let restocked = null;

    for (const d of drops) {
      const nowAvailable = isAvailable(d);
      const wasSoldOut = snap[d.slug] === "sold_out";
      nextSnap[d.slug] = nowAvailable ? "available" : "sold_out";
      // Detect: was sold out, is now available → restock!
      if (wasSoldOut && nowAvailable && !restocked) {
        restocked = { slug: d.slug, name: d.name, image_url: d.image_url };
      }
    }
    saveSnapshot(nextSnap);

    if (restocked) {
      fireConfetti();
      setToast(restocked);
      if (dismissRef.current) clearTimeout(dismissRef.current);
      dismissRef.current = setTimeout(() => setToast(null), 12_000);
    }
  }, [user]);

  useEffect(() => {
    if (!user?.is_member) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    // Small delay so we don't hit the API before the page has stabilised
    const startId = setTimeout(check, 3000);
    timerRef.current = setInterval(check, POLL_MS);
    return () => {
      clearTimeout(startId);
      if (timerRef.current) clearInterval(timerRef.current);
      if (dismissRef.current) clearTimeout(dismissRef.current);
    };
  }, [user, check]);

  if (!toast) return null;

  return (
    <div
      className="fixed top-24 right-6 z-[80] max-w-sm animate-in slide-in-from-right-4 fade-in duration-500"
      data-testid="restock-reveal-toast"
      role="status"
      aria-live="polite"
    >
      <div className="bg-[var(--ff-primary)] text-white rounded-2xl shadow-xl border border-[var(--ff-primary-hover)] overflow-hidden">
        <div className="flex items-start gap-3 p-4 pr-3">
          {toast.image_url && (
            <img
              src={toast.image_url}
              alt=""
              className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[var(--ff-accent-kraft)]">
              <Sparkles size={12} /> Back in stock
            </div>
            <p className="font-display text-base leading-tight mt-1 truncate" data-testid="restock-reveal-name">
              {toast.name}
            </p>
            <Link
              to={`/drops/${toast.slug}`}
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-white hover:text-[var(--ff-accent-kraft)] transition-colors"
              data-testid="restock-reveal-cta"
              onClick={() => setToast(null)}
            >
              Grab yours <ChevronRight size={12} />
            </Link>
          </div>
          <button
            type="button"
            onClick={() => setToast(null)}
            className="text-white/60 hover:text-white transition-colors flex-shrink-0"
            aria-label="Dismiss restock alert"
            data-testid="restock-reveal-dismiss"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
